import { useState } from 'react'
import { BarChart3, Copy, CheckCircle, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

const BASE = typeof window !== 'undefined'
  ? `${window.location.protocol}//${window.location.hostname}:8000`
  : 'https://your-karyva-instance.com'

const BI_ENDPOINTS = [
  {
    path: '/api/v1/bi/project-health',
    label: 'Project Health',
    description: 'One row per project — status, progress, overdue flag, open RAID counts, milestone completion.',
    powerbi: 'Project Health dashboard page',
  },
  {
    path: '/api/v1/bi/budget-variance',
    label: 'Budget Variance',
    description: 'Budget vs actual cost with variance amount and percentage per project.',
    powerbi: 'Budget Variance page',
  },
  {
    path: '/api/v1/bi/resource-utilization',
    label: 'Resource Utilization',
    description: 'Task breakdown (total / completed / in-progress / blocked) with completion rate per project.',
    powerbi: 'Resource Utilization page',
  },
  {
    path: '/api/v1/bi/raid-summary',
    label: 'RAID Summary',
    description: 'RAID items grouped by project, type, and status — suitable for drill-down tables.',
    powerbi: 'RAID Summary page',
  },
]

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const handle = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    toast.success('Copied')
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button
      onClick={handle}
      aria-label="Copy to clipboard"
      className="p-1.5 min-w-[36px] min-h-[36px] flex items-center justify-center rounded hover:bg-[#e8f0f8] transition-colors text-[#486581]"
    >
      {copied ? <CheckCircle size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
    </button>
  )
}

export default function BIConnectorPage() {
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <BarChart3 size={24} className="text-[#486581]" aria-hidden="true" />
          <h1 className="text-2xl font-semibold text-[#102a43]">BI Connector</h1>
        </div>
        <p className="text-sm text-[#627d98]">
          Connect Power BI or Tableau directly to live Karyva data using read-only API tokens.
          No scheduled exports needed.
        </p>
      </div>

      {/* Prerequisites */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 flex gap-3">
        <AlertCircle size={18} className="text-blue-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="text-sm text-blue-800">
          <p className="font-semibold mb-1">Before you start</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>
              Create a <strong>read-only</strong> API token in{' '}
              <a href="/settings" className="underline hover:no-underline">Admin Settings → API &amp; Webhooks</a>.
            </li>
            <li>Use the token as the <code className="bg-blue-100 px-1 rounded">X-API-Key</code> header value in your BI tool's web connector.</li>
            <li>All endpoints return JSON arrays — compatible with Power BI Web connector and Tableau Web Data Connector.</li>
          </ol>
        </div>
      </div>

      {/* Endpoints */}
      <div className="space-y-4">
        <h2 className="font-semibold text-[#102a43] text-lg">Available Endpoints</h2>
        {BI_ENDPOINTS.map(ep => {
          const fullUrl = `${BASE}${ep.path}`
          return (
            <div key={ep.path} className="bg-white border-2 border-[#bcccdc] rounded-lg p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-[#102a43]">{ep.label}</h3>
                  <p className="text-sm text-[#627d98] mt-0.5">{ep.description}</p>
                </div>
                <span className="text-xs bg-green-100 text-green-700 border border-green-300 rounded px-2 py-0.5 flex-shrink-0 font-medium">
                  GET
                </span>
              </div>

              <div className="flex items-center gap-2 bg-[#f0f4f8] rounded border border-[#bcccdc] px-3 py-2">
                <code className="text-xs flex-1 text-[#334e68] font-mono break-all">{fullUrl}</code>
                <CopyButton text={fullUrl} />
              </div>

              <p className="text-xs text-[#829ab1]">
                Power BI page: <strong>{ep.powerbi}</strong>
              </p>
            </div>
          )
        })}
      </div>

      {/* Power BI connection guide */}
      <div className="bg-white border-2 border-[#bcccdc] rounded-lg p-5 space-y-4">
        <h2 className="font-semibold text-[#102a43] text-lg">Power BI — Step-by-step</h2>
        <ol className="space-y-3 text-sm text-[#334e68]">
          {[
            'Open Power BI Desktop → Get Data → Web.',
            'Enter the endpoint URL (e.g. /api/v1/bi/project-health) and click OK.',
            'In the Web connector dialog, choose "Advanced" and add a Custom Header:',
            'Header name: X-API-Key  |  Header value: <your read-only token>',
            'Click Connect. Power BI loads the JSON array and expands it into a table.',
            'Repeat for each endpoint you need, then build your report using the four tables.',
          ].map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#486581] text-white text-xs flex items-center justify-center font-bold" aria-hidden="true">
                {i + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Tableau guide */}
      <div className="bg-white border-2 border-[#bcccdc] rounded-lg p-5 space-y-4">
        <h2 className="font-semibold text-[#102a43] text-lg">Tableau — Step-by-step</h2>
        <ol className="space-y-3 text-sm text-[#334e68]">
          {[
            'Connect → JSON File (or use Tableau Web Data Connector).',
            'Paste the endpoint URL with your API token as a query parameter:',
          ].map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#486581] text-white text-xs flex items-center justify-center font-bold" aria-hidden="true">
                {i + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
        <div className="flex items-center gap-2 bg-[#f0f4f8] rounded border border-[#bcccdc] px-3 py-2">
          <code className="text-xs flex-1 text-[#334e68] font-mono break-all">
            {BASE}/api/v1/bi/project-health
          </code>
        </div>
        <p className="text-xs text-[#829ab1]">
          Pass <code className="bg-[#f0f4f8] px-1 rounded">X-API-Key</code> via the REST connector headers or use a Tableau WDC script.
        </p>
      </div>

      {/* Refresh cadence note */}
      <div className="text-xs text-[#829ab1] text-center pb-4">
        Data is live — refresh your BI report to get the latest figures. Power BI scheduled refresh is supported with Direct Query or Import mode.
      </div>
    </div>
  )
}
